import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import Tooltip from '../../components/gui/tooltip';
import { outgoingCash } from '../../lib/saleReportCash';
import { computeReportTotals, explainCashDifference } from '../../lib/saleReportTotals';

// Shared "Omsetning / Betalinger / Avstemming" block for the Dyrejournal report page
// (reports.js, editable) and the printed report (reportPrint.js, static).
//
// One table, one column per payment method, three core rows:
//   Salg (omsetning) = sales recognised in the period by saleDate (bokføringsloven § 4 / mval. § 15-9),
//                      by the payment method MARKED ON THE SALE, grouped kontantsalg / kredittsalg / korreksjoner.
//   Innbetalt        = money actually received in the period by paidDate (salePayment), same columns.
//   Forskjell        = Innbetalt minus Salg per column.
// Then the cash rows and the count/deviation inputs the dagsoppgjør needs (bokføringsforskriften § 5-3-3).
// Avstemming = kontantsalg vs cash-type payments, kredittsalg vs credit payments.
//
// Props:
//   saleReport     object with sale* (sale-based) and amount* (payment-based) figures
//   reportType     'day' | 'periode' | 'z-report' | 'month' | 'year'
//   showCreditCard boolean feature flag (featureFlags.creditCard)
//   showConvene    boolean feature flag (featureFlags.convenePayment): adds a Convene column under Kredittsalg
//   hideCreditSales boolean, screen only (ignored in print): drops the Kredittsalg columns and the credit
//                  rows of the reconciliation so the morning cash count only shows kontant/kort/Vipps.
//                  The turnover total, the print-out and the saved report are not affected.
//   editable       render inputs (report page) instead of saved values (print)
//   inputFor       field => onInput handler for `saleReport.<field>` (only used when editable)
//   cardBreakdown  optional NETS breakdown { totalAmount } for the card column split
//   print          smaller font, no tooltips
//   onAnalyze      click handler that loads the cash-flow analysis (enables the explanation section)
//   cashflowAnalysis / cashflowAnalysisLoading   result and loading flag of that analysis

const RIGHT = 'text-align: right;';
const LEFT = 'text-align: left;';
const SEPARATOR = 'border-top: 1px solid #808080;';

const DAILY_TYPES = ['day', 'periode', 'z-report'];

class DyrejournalReportSalesAndPayments extends Component {
    fmt = value => <nobr>{util.format(value || 0, 2)}</nobr>;

    help = (node, opts = {}) => {
        const { print } = this.props;
        if (print) {
            return null;
        }
        // fixed: the tables live in .table-responsive wrappers which would clip an absolute popup
        return <Tooltip text={<i class='fa-duotone fa-circle-info ms-2' />} fixed {...opts}>{node}</Tooltip>;
    }

    field = (name, { type = 'number', rows } = {}) => {
        const { saleReport = {}, editable, inputFor } = this.props;
        if (!editable) {
            if (rows) {
                return saleReport[name] || '';
            }
            return this.fmt(saleReport[name]);
        }
        if (rows) {
            return <textarea class='form-control form-control-sm' rows={rows} name={name} placeholder='' value={saleReport[name] ?? ''} onInput={inputFor(name)} />;
        }
        return <input size='3' type={type} class='form-control form-control-sm text-end' name={name} placeholder='' value={saleReport[name] ?? ''} onInput={inputFor(name)} />;
    }

    // The print-out and the saved report must stay complete, so the switch is screen only.
    hideCredit = () => {
        const { hideCreditSales, print } = this.props;
        return !!hideCreditSales && !print;
    }

    renderSalesPayments(totals) {
        const { saleReport = {}, reportType, showCreditCard, showConvene, cardBreakdown, print } = this.props;
        const hideCredit = this.hideCredit();
        const tableStyle = `font-size: ${print ? '0.75em' : '0.9em'}; line-height: 1.0em;`;
        const isDaily = DAILY_TYPES.includes(reportType);
        const cashCols = showCreditCard ? 4 : 3;
        let creditCols = showConvene ? 4 : 3;
        if (hideCredit) {
            creditCols = 0;
        }
        // kontantsalg + kredittsalg + korreksjoner (2) + annet (1) + omsetning/rabatt/mva (3)
        const dataCols = cashCols + creditCols + 6;
        const minus = (a, b) => (Math.round((Number(a) || 0) * 100) - Math.round((Number(b) || 0) * 100)) / 100;
        const blank = count => Array.from({ length: count }, (_, idx) => <td key={idx} style={RIGHT} />);
        // A row where only the cash column carries content (opening cash, change, withdrawals, count).
        const cashOnlyRow = (row, label, content, style) => (
            <tr data-row={row} style={style}>
                <td style={LEFT}>{label}</td>
                <td style={RIGHT}>{content}</td>
                {blank(dataCols - 1)}
            </tr>
        );
        const netsRow = (row, label, value) => (
            <tr class='text-body-secondary' data-row={row}>
                <td style={`${LEFT} padding-left: 2em;`}><small>— {label}</small></td>
                <td style={RIGHT} />
                <td style={RIGHT}><small>{this.fmt(value)}</small></td>
                {blank(dataCols - 2)}
            </tr>
        );
        return (
            <div class='table-responsive' data-section='sales-payments'>
                <h6 class='mb-2 border-bottom'>
                    <Text id='dyrejournal.report.sales-payments-section'>Sales and payments</Text>
                </h6>
                <table class='table table-sm table-hover table-striped font-style-narrow' style={tableStyle}>
                    <thead>
                        <tr class='text-body-secondary'>
                            <th>&nbsp;</th>
                            <th colspan={cashCols} class='border-start text-center'>
                                <Text id='dyrejournal.report.cash-sales'>Cash sales</Text>
                                {this.help(<Text id='dyrejournal.report.cash-sales-help'>Sales marked as paid at the counter with cash, card or Vipps.</Text>)}
                            </th>
                            {!hideCredit && <th colspan={creditCols} class='border-start text-center'>
                                <Text id='dyrejournal.report.credit-sales'>Credit sales</Text>
                                {this.help(<Text id='dyrejournal.report.credit-sales-help'>Sales marked as invoice, account or partial. Payment comes later and is shown under Payments. Partial sales paid in full with cash, card or Vipps are counted as cash sales.</Text>)}
                            </th>}
                            <th colspan={2} class='border-start text-center'>
                                <Text id='dyrejournal.report.corrections'>Corrections</Text>
                                {this.help(<Text id='dyrejournal.report.corrections-help'>Credit notes and credited sales that reduce the turnover.</Text>)}
                            </th>
                            <th class='border-start'>&nbsp;</th>
                            <th colspan={3} class='border-start'>&nbsp;</th>
                        </tr>
                        <tr>
                            <th>&nbsp;</th>
                            <th style={RIGHT} class='border-start' data-col='cash'><Text id='dyrejournal.report.cashregister'>Cash</Text></th>
                            <th style={RIGHT} data-col='card'><Text id='dyrejournal.report.bankcard'>Card</Text></th>
                            {showCreditCard && <th style={RIGHT} data-col='creditCard'><Text id='dyrejournal.report.creditcard'>Credit Card</Text></th>}
                            <th style={RIGHT} data-col='vipps'><Text id='dyrejournal.report.vipps'>Vipps</Text></th>
                            {!hideCredit && <>
                                <th style={RIGHT} class='border-start' data-col='invoice'><Text id='dyrejournal.report.invoice'>Invoice</Text></th>
                                <th style={RIGHT} data-col='account'><Text id='dyrejournal.report.account'>Account</Text></th>
                                <th style={RIGHT} data-col='partial'>
                                    <Text id='dyrejournal.report.partial'>Partial</Text>
                                    {this.help(<Text id='dyrejournal.report.partial-column-help'>Sales paid with several payment methods that are not yet settled, or settled partly on invoice, account or insurance. A partial sale paid in full with cash, card or Vipps is counted under Cash sales, split by its payments.</Text>, { left: -150, width: 260 })}
                                </th>
                                {showConvene && <th style={RIGHT} data-col='convene'><Text id='dyrejournal.report.convene'>Convene</Text></th>}
                            </>}
                            <th style={RIGHT} class='border-start' data-col='creditNote'><Text id='dyrejournal.report.creditNote'>Credit note</Text></th>
                            <th style={RIGHT} data-col='saleCredited'><Text id='dyrejournal.report.saleCredited'>Sale credited</Text></th>
                            <th style={RIGHT} class='border-start' data-col='other'>
                                <Text id='dyrejournal.report.other-payments'>Other</Text>
                                {this.help(<>
                                    <Text id='dyrejournal.report.sales-without-method-help'>Sales in the turnover that are not marked with any of the payment methods above, for example unpaid sales.</Text>
                                    {' '}
                                    <Text id='dyrejournal.report.other-payments-help'>All other payment records: insurance, rounding, refunds by credit note, Convene and payments without a method.</Text>
                                </>, { left: -150, width: 260 })}
                            </th>
                            <th style={RIGHT} class='border-start' data-col='turnover'>
                                <Text id='dyrejournal.report.turnover'>Turnover</Text> / <Text id='dyrejournal.report.total'>Total</Text>
                                {this.help(<Text id='dyrejournal.report.turnover-help'>Turnover is total sale for this periode. Payment is not taken into account. This is your total for this periode.</Text>, { left: -150, width: 230 })}
                            </th>
                            <th style={RIGHT} data-col='discount'>
                                <Text id='dyrejournal.report.discount'>Discount</Text>
                                {this.help(<Text id='dyrejournal.report.discount-help'>Discount is the total discount including tax for this periode. Payment is not taken into account. This is your total discount for this periode.</Text>, { left: -150, width: 230 })}
                            </th>
                            <th style={RIGHT} data-col='tax'>
                                <Text id='dyrejournal.report.tax'>Tax inc</Text>
                                {this.help(<Text id='dyrejournal.report.tax-help'>Tax included in the total sale.</Text>, { left: -180, width: 200 })}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr data-row='sales'>
                            <td style={LEFT}>
                                <Text id='dyrejournal.report.sales-row'>Sales (turnover)</Text>
                                {this.help(<Text id='dyrejournal.report.turnover-section-help'>Sales recognised in the period by sale date, regardless of payment. Columns show the payment method marked on the sale. This is the basis for bookkeeping and VAT.</Text>, { width: 260 })}
                            </td>
                            <td style={RIGHT} class='border-start'>{this.fmt(saleReport.saleAmountCash)}</td>
                            <td style={RIGHT}>{this.fmt(saleReport.saleAmountCard)}</td>
                            {showCreditCard && <td style={RIGHT}>{this.fmt(saleReport.saleAmountCreditCard)}</td>}
                            <td style={RIGHT}>{this.fmt(saleReport.saleAmountVipps)}</td>
                            {!hideCredit && <>
                                <td style={RIGHT} class='border-start'>{this.fmt(saleReport.amountInvoice)}</td>
                                <td style={RIGHT}>{this.fmt(saleReport.saleAmountAccount)}</td>
                                <td style={RIGHT}>{this.fmt(saleReport.amountPartial)}</td>
                                {showConvene && <td style={RIGHT}>{this.fmt(totals.conveneSales)}</td>}
                            </>}
                            <td style={RIGHT} class='border-start'>{this.fmt(saleReport.amountCreditNote)}</td>
                            <td style={RIGHT}>{this.fmt(saleReport.amountSaleCredited)}</td>
                            <td style={RIGHT} class='border-start'>{this.fmt(totals.salesWithoutMethod)}</td>
                            <td style={RIGHT} class='border-start'><strong>{this.fmt(saleReport.amountTotal)}</strong></td>
                            <td style={RIGHT}>{this.fmt(saleReport.amountDiscount)}</td>
                            <td style={RIGHT}>{this.fmt(saleReport.amountTax)}</td>
                        </tr>
                        {cardBreakdown && netsRow('with-nets', <Text id='dyrejournal.report.with-nets'>with NETS</Text>, cardBreakdown.totalAmount)}
                        {cardBreakdown && netsRow('without-nets', <Text id='dyrejournal.report.without-nets'>manual (without NETS)</Text>, minus(saleReport.saleAmountCard, cardBreakdown.totalAmount))}
                        {isDaily && cashOnlyRow(
                            'opening-cash',
                            saleReport.openingCash != null
                                ? <Text id='dyrejournal.report.opening-cash'>Opening cash</Text>
                                : <Text id='dyrejournal.report.outgoing-last-report'>Outgoing last report</Text>,
                            this.fmt(saleReport.openingCash),
                        )}
                        <tr data-row='payments'>
                            <td style={LEFT}>
                                {isDaily ? '+ ' : ''}
                                <Text id='dyrejournal.report.payments-received'>Received</Text>
                                {this.help(<Text id='dyrejournal.report.payments-section-help'>Money actually received in the period by payment date, per payment method. Payments for sales from earlier periods are included; sales not yet paid are not.</Text>, { width: 260 })}
                            </td>
                            <td style={RIGHT} class='border-start'>{this.fmt(saleReport.amountCash)}</td>
                            <td style={RIGHT}>{this.fmt(saleReport.amountCard)}</td>
                            {showCreditCard && <td style={RIGHT}>{this.fmt(saleReport.amountCreditCard)}</td>}
                            <td style={RIGHT}>{this.fmt(saleReport.amountVipps)}</td>
                            {!hideCredit && <>
                                <td style={RIGHT} class='border-start'>{this.fmt(saleReport.amountInvoicePaid)}</td>
                                <td style={RIGHT}>{this.fmt(saleReport.amountAccount)}</td>
                                <td style={RIGHT} />
                                {showConvene && <td style={RIGHT}>{this.fmt(totals.convenePayments)}</td>}
                            </>}
                            <td style={RIGHT} class='border-start' />
                            <td style={RIGHT} />
                            <td style={RIGHT} class='border-start'>{this.fmt(totals.otherPayments)}</td>
                            <td style={RIGHT} class='border-start'><strong>{this.fmt(totals.paymentsTotal)}</strong></td>
                            <td style={RIGHT} />
                            <td style={RIGHT} />
                        </tr>
                        <tr data-row='difference' class='text-body-secondary' style={SEPARATOR}>
                            <td style={LEFT}>
                                = <Text id='dyrejournal.report.difference'>Difference</Text>
                                {this.help(<Text id='dyrejournal.report.invoice-bank-note'>Invoice sales are normally paid as a Bank payment (or as Invoice paid when the payment is registered on the invoice). Read the Invoice and Bank differences together, see Credit sales under Reconciliation.</Text>, { width: 260 })}
                            </td>
                            <td style={RIGHT} class='border-start'>{this.fmt(minus(saleReport.amountCash, saleReport.saleAmountCash))}</td>
                            <td style={RIGHT}>{this.fmt(minus(saleReport.amountCard, saleReport.saleAmountCard))}</td>
                            {showCreditCard && <td style={RIGHT}>{this.fmt(minus(saleReport.amountCreditCard, saleReport.saleAmountCreditCard))}</td>}
                            <td style={RIGHT}>{this.fmt(minus(saleReport.amountVipps, saleReport.saleAmountVipps))}</td>
                            {!hideCredit && <>
                                <td style={RIGHT} class='border-start'>{this.fmt(minus(saleReport.amountInvoicePaid, saleReport.amountInvoice))}</td>
                                <td style={RIGHT}>{this.fmt(minus(saleReport.amountAccount, saleReport.saleAmountAccount))}</td>
                                <td style={RIGHT}>{this.fmt(minus(0, saleReport.amountPartial))}</td>
                                {showConvene && <td style={RIGHT}>{this.fmt(minus(totals.convenePayments, totals.conveneSales))}</td>}
                            </>}
                            <td style={RIGHT} class='border-start'>{this.fmt(minus(0, saleReport.amountCreditNote))}</td>
                            <td style={RIGHT}>{this.fmt(minus(0, saleReport.amountSaleCredited))}</td>
                            <td style={RIGHT} class='border-start'>{this.fmt(minus(totals.otherPayments, totals.salesWithoutMethod))}</td>
                            <td style={RIGHT} class='border-start'><strong>{this.fmt(minus(totals.paymentsTotal, saleReport.amountTotal))}</strong></td>
                            <td style={RIGHT} />
                            <td style={RIGHT} />
                        </tr>
                        {isDaily && cashOnlyRow('added-change', <>+ <Text id='dyrejournal.report.added-change'>Added change</Text></>, this.field('addedChange'))}
                        {isDaily && cashOnlyRow('bank-withdrawal', <>- <Text id='dyrejournal.report.bank-withdrawal'>Bank withdrawal</Text></>, this.field('bankWithdrawal'))}
                        {isDaily && cashOnlyRow('outgoing-cash', <>= <Text id='dyrejournal.report.outgoing-cash'>Outgoing cash</Text></>, this.fmt(outgoingCash(saleReport)), SEPARATOR)}
                        {cashOnlyRow('counted-cash', <Text id='dyrejournal.report.counted-cash'>Counted cash</Text>, this.field('countedCash'))}
                        <tr data-row='deviation'>
                            <td style={LEFT}><Text id='dyrejournal.report.deviation'>Deviation</Text></td>
                            <td style={RIGHT} class='border-start'>{this.field('deviationCash')}</td>
                            <td style={RIGHT}>{this.field('deviationCard')}</td>
                            {showCreditCard && <td style={RIGHT}>{this.field('deviationCreditCard')}</td>}
                            <td style={RIGHT}>{this.field('deviationVipps')}</td>
                            {!hideCredit && <>
                                <td style={RIGHT} class='border-start' />
                                <td style={RIGHT}>{this.field('deviationAccount')}</td>
                                <td style={RIGHT} />
                                {showConvene && <td style={RIGHT} />}
                            </>}
                            <td style={RIGHT} class='border-start' />
                            <td style={RIGHT} />
                            <td style={RIGHT} class='border-start' />
                            <td style={RIGHT} class='border-start'>{this.field('deviationTotal')}</td>
                            <td style={RIGHT} />
                            <td style={RIGHT} />
                        </tr>
                        <tr data-row='comment'>
                            <td style={LEFT}><Text id='dyrejournal.report.comment'>Comment</Text></td>
                            <td style={LEFT} colspan={dataCols}>{this.field('comment', { rows: 2 })}</td>
                        </tr>
                    </tbody>
                </table>
                {!hideCredit && <div class='small text-body-secondary' data-note='invoice-bank'>
                    <Text id='dyrejournal.report.invoice-bank-note'>Invoice sales are normally paid as a Bank payment (or as Invoice paid when the payment is registered on the invoice). Read the Invoice and Bank differences together, see Credit sales under Reconciliation.</Text>
                </div>}
            </div>
        );
    }

    renderReconciliation(totals) {
        const { print } = this.props;
        const hideCredit = this.hideCredit();
        const tableStyle = `font-size: ${print ? '0.75em' : '0.9em'}; line-height: 1.0em;`;
        const row = (key, label, value, { strong = false, muted = false } = {}) => (
            <tr data-row={key} class={muted ? 'text-body-secondary' : ''} style={strong ? SEPARATOR : ''}>
                <td>{strong ? <strong>= {label}</strong> : label}</td>
                <td style={RIGHT}>{strong ? <strong>{this.fmt(value)}</strong> : this.fmt(value)}</td>
            </tr>
        );
        return (
            <div class='table-responsive mt-4' data-section='reconciliation'>
                <h6 class='mb-2 border-bottom'>
                    <Text id='dyrejournal.report.reconciliation-section'>Reconciliation</Text>
                    {this.help(<Text id='dyrejournal.report.difference-help'>A positive difference means more was received than sold in the period (payments for earlier sales). A negative difference means sales in the period are not yet paid.</Text>, { width: 260 })}
                </h6>
                <table class='table table-sm table-hover table-striped font-style-narrow' style={tableStyle}>
                    <thead>
                        <tr>
                            <th><Text id='dyrejournal.report.description'>Description</Text></th>
                            <th style={RIGHT}><Text id='dyrejournal.report.amount'>Amount</Text></th>
                        </tr>
                    </thead>
                    <tbody>
                        {row('cash-sales', <Text id='dyrejournal.report.cash-sales'>Cash sales</Text>, totals.cashSales)}
                        {row('cash-payments', <Text id='dyrejournal.report.cash-payments'>Received cash / card / Vipps</Text>, totals.cashPayments)}
                        {row('cash-difference', <Text id='dyrejournal.report.cash-difference'>Difference cash sales</Text>, totals.cashDifference, { strong: true })}
                        {!hideCredit && <>
                            {row('credit-sales', <Text id='dyrejournal.report.credit-sales'>Credit sales</Text>, totals.creditSales)}
                            {row('credit-payments', <Text id='dyrejournal.report.credit-payments'>Received on credit sales</Text>, totals.creditPayments)}
                            {row('credit-difference', <Text id='dyrejournal.report.credit-difference'>Difference credit sales</Text>, totals.creditDifference, { strong: true })}
                        </>}
                    </tbody>
                </table>
                {!hideCredit && <div class='small text-body-secondary'>
                    <Text id='dyrejournal.report.credit-difference-note'>The credit sales difference is invoices issued in the period that are not yet paid, and payments received on older invoices.</Text>
                </div>}
            </div>
        );
    }

    renderExplanation(totals) {
        const { onAnalyze, cashflowAnalysis, cashflowAnalysisLoading, print } = this.props;
        if (print || !onAnalyze) {
            return null;
        }
        const tableStyle = 'font-size: 0.9em; line-height: 1.0em;';
        const e = explainCashDifference(cashflowAnalysis, totals.cashDifference);
        const row = (key, sign, label, value, { strong = false } = {}) => (
            <tr data-row={key} style={strong ? SEPARATOR : ''}>
                <td>{strong ? <strong>{sign} {label}</strong> : <>{sign} {label}</>}</td>
                <td style={RIGHT}>{strong ? <strong>{this.fmt(value)}</strong> : this.fmt(value)}</td>
            </tr>
        );
        return (
            <div class='table-responsive mt-4' data-section='explanation'>
                <h6 class='mb-2 border-bottom'>
                    <Text id='dyrejournal.report.explanation-section'>Explanation of the cash sales difference</Text>
                    {this.help(<Text id='dyrejournal.report.explanation-section-help'>Breaks the cash sales difference down into payments for earlier sales, counter payments on credit sales, overpayments and cash sales not yet paid. Based on every sale and payment in the period.</Text>, { width: 260 })}
                    <span class='badge bg-warning text-dark ms-2'><Text id='dyrejournal.report.experimental'>Experimental</Text></span>
                </h6>
                {cashflowAnalysisLoading && <div class='text-center py-3'>
                    <i class='fa-duotone fa-spinner fa-spin me-2' />
                    <Text id='dyrejournal.report.loading'>Loading...</Text>
                </div>}
                {!cashflowAnalysisLoading && !cashflowAnalysis && <button type='button' class='btn btn-sm btn-outline-secondary' onClick={onAnalyze}>
                    <i class='fa-duotone fa-magnifying-glass-chart me-2' />
                    <Text id='dyrejournal.report.run-analysis'>Explain the difference</Text>
                </button>}
                {!cashflowAnalysisLoading && cashflowAnalysis && <table class='table table-sm table-hover table-striped font-style-narrow' style={tableStyle}>
                    <thead>
                        <tr>
                            <th><Text id='dyrejournal.report.description'>Description</Text></th>
                            <th style={RIGHT}><Text id='dyrejournal.report.amount'>Amount</Text></th>
                        </tr>
                    </thead>
                    <tbody>
                        {row('previous-periods', '+', <Text id='dyrejournal.report.payments-previous-periods'>Payments received for sales from previous periods</Text>, e.previousPeriods)}
                        {row('credit-paid-at-counter', '+', <Text id='dyrejournal.report.credit-paid-at-counter'>Counter payments on credit sales in the period</Text>, e.creditSalesPaidAtCounter)}
                        {row('overpaid', '+', <Text id='dyrejournal.report.overpaid-cash-sales'>Overpaid cash sales</Text>, e.overpaid)}
                        {row('unpaid-cash-sales', '-', <Text id='dyrejournal.report.unpaid-cash-sales'>Cash sales not paid in the period</Text>, e.unpaidCashSales)}
                        {row('calculated-diff', '=', <Text id='dyrejournal.report.calculated-diff'>Calculated difference</Text>, e.calculatedDiff, { strong: true })}
                        {row('cash-difference-ref', '', <Text id='dyrejournal.report.cash-difference'>Difference cash sales</Text>, totals.cashDifference)}
                        {row('unexplained', '=', <Text id='dyrejournal.report.unexplained-rest'>Unexplained remainder</Text>, e.unexplained, { strong: true })}
                    </tbody>
                </table>}
            </div>
        );
    }

    render() {
        const { saleReport = {}, showCreditCard, showConvene } = this.props;
        const totals = computeReportTotals(saleReport, { showCreditCard, showConvene });
        return (
            <>
                {this.renderSalesPayments(totals)}
                {this.renderReconciliation(totals)}
                {this.renderExplanation(totals)}
            </>
        );
    }
}

export default DyrejournalReportSalesAndPayments;
